import { useEffect, useMemo, useState } from 'react';
import s from './attach-file-modal.module.scss';
import classNames from 'classnames';
import { IconCheck, IconSearch, IconTrash } from '@tabler/icons-react';
import { FetchingDiv } from '@shared/ui/fetching-div/fetching-div';
import { useAttachments, useDeleteAttachment, parseSize } from '@entities/attachment';
import { FileTypeIcon, getFileTypeCategory } from '../../model/file-type-icon';
import { isImage } from '@utils/lib/isImage';

const isAttachmentSelected = (list, item) =>
	list.some((file) => file === item || (file?.id != null && file.id === item?.id));

const FileThumb = ({ file }) => {
	if (file?.file && isImage({ name: file.name || `file.${file.extension}` })) {
		return (
			<span className={s.thumb}>
				<img
					className={s.thumbImage}
					src={`${process.env.REACT_APP_STORAGE_URL}${file.file}`}
					alt=""
					loading="lazy"
				/>
			</span>
		);
	}

	return <FileTypeIcon extension={file?.extension} />;
};

/** Вкладка «Мои файлы» — выбор из ранее загруженных вложений пользователя. */
export const MyFilesTab = ({ selected, multiple, maxFiles, onlyCategory, maxSize, onToggle, onDeleted }) => {
	const { data, isLoading, isError } = useAttachments();
	const { mutate: deleteAttachment } = useDeleteAttachment();
	const [search, setSearch] = useState('');
	const [confirmingId, setConfirmingId] = useState(null);

	// Сброс подтверждения удаления, если не подтвердили за 3 секунды
	useEffect(() => {
		if (confirmingId === null) return undefined;
		const timer = setTimeout(() => setConfirmingId(null), 3000);
		return () => clearTimeout(timer);
	}, [confirmingId]);

	const visibleFiles = useMemo(() => {
		let list = data || [];
		if (onlyCategory) {
			list = list.filter((file) => getFileTypeCategory(file.extension) === onlyCategory);
		}
		if (maxSize) {
			list = list.filter((file) => parseSize(file.size) <= maxSize);
		}
		return list;
	}, [data, onlyCategory, maxSize]);

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return visibleFiles;
		return visibleFiles.filter((file) => (file.name || '').toLowerCase().includes(query));
	}, [visibleFiles, search]);

	const atLimit = selected.length >= maxFiles;

	const handleDelete = (event, file) => {
		event.stopPropagation();
		if (confirmingId !== file.id) {
			setConfirmingId(file.id);
			return;
		}
		setConfirmingId(null);
		deleteAttachment(file.id, {
			onSuccess: () => onDeleted?.(file),
		});
	};

	const handleRowKeyDown = (event, file) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		onToggle(file);
	};

	return (
		<div className={s.tabBody}>
			<div className={s.search}>
				<IconSearch size={18} />
				<input
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					placeholder="Поиск по названию"
				/>
			</div>

			<FetchingDiv className={s.fileList} isFetching={isLoading}>
				{isError ? (
					<p className={s.emptyText}>Не удалось загрузить файлы. Попробуйте позже.</p>
				) : filtered.length === 0 ? (
					<p className={s.emptyText}>
						{search
							? 'Ничего не найдено'
							: (data || []).length > 0 && visibleFiles.length === 0
								? onlyCategory === 'video'
									? 'В «Моих файлах» нет подходящих видео'
									: 'В «Моих файлах» нет подходящих изображений'
								: 'Здесь появятся ранее загруженные файлы. Прикрепите файл с устройства — он сохранится в этом списке.'}
					</p>
				) : (
					filtered.map((file) => {
						const isSelected = isAttachmentSelected(selected, file);
						const isConfirming = confirmingId === file.id;

						return (
							<div
								key={file.id}
								role="button"
								tabIndex={0}
								className={classNames(
									s.fileRow,
									isSelected && s.selected,
									!isSelected && atLimit && s.disabled
								)}
								onClick={() => onToggle(file)}
								onKeyDown={(event) => handleRowKeyDown(event, file)}
								title={file.name || 'Файл'}
							>
								<FileThumb file={file} />
								<span className={s.fileMeta}>
									<span className={s.fileName}>{file.name || 'Файл'}</span>
									<span className={s.fileSub}>
										{[file.sizeLabel, file.dateLabel].filter(Boolean).join(' · ')}
									</span>
								</span>
								{isSelected && <IconCheck className={s.checkIcon} size={20} />}
								<button
									type="button"
									className={classNames(s.removeBtn, isConfirming && s.confirming)}
									onClick={(event) => handleDelete(event, file)}
									title={isConfirming ? 'Нажмите ещё раз, чтобы удалить' : 'Удалить файл'}
								>
									<IconTrash size={16} />
								</button>
							</div>
						);
					})
				)}
			</FetchingDiv>
		</div>
	);
};
